import { Injectable, NgZone } from '@angular/core';

export interface ToastAction { label: string; action: () => void; }
export interface ToastOptions {
  type?: 'succes' | 'erreur' | 'info' | 'attention';
  /** Durée en ms (défaut 5 s ; 8 s avec une action). 0 = reste affiché jusqu'à fermeture */
  duree?: number;
  actions?: ToastAction[];
}

/**
 * Toasts accessibles (aria-live), en bas de l'écran, avec actions (Annuler, Voir…).
 * Rendu DOM direct : utilisable depuis n'importe quel écran sans déclaration de composant.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private zone?: HTMLElement;

  constructor(private ngZone: NgZone) {}

  succes(message: string, opts: ToastOptions = {}) { return this.afficher(message, { ...opts, type: 'succes' }); }
  erreur(message: string, opts: ToastOptions = {}) { return this.afficher(message, { duree: 8000, ...opts, type: 'erreur' }); }
  info(message: string, opts: ToastOptions = {}) { return this.afficher(message, { ...opts, type: 'info' }); }
  attention(message: string, opts: ToastOptions = {}) { return this.afficher(message, { duree: 8000, ...opts, type: 'attention' }); }

  /** Message d'erreur HTTP lisible (message serveur si présent) */
  erreurHttp(err: any, defaut = 'Une erreur est survenue. Vérifiez votre connexion et réessayez.') {
    const msg = err?.error?.message || (err?.status === 0 ? 'Connexion impossible : vérifiez votre réseau.' : defaut);
    return this.erreur(msg);
  }

  afficher(message: string, opts: ToastOptions = {}): () => void {
    const zone = this.conteneur();
    const type = opts.type || 'info';
    const duree = opts.duree ?? (opts.actions?.length ? 8000 : 5000);
    const el = document.createElement('div');
    el.className = `bay-toast bay-toast-${type}`;
    el.setAttribute('role', type === 'erreur' ? 'alert' : 'status');
    const icone = { succes: 'check_circle', erreur: 'error', info: 'info', attention: 'warning' }[type];
    const ic = document.createElement('span');
    ic.className = 'material-symbols-outlined bay-toast-icon';
    ic.setAttribute('aria-hidden', 'true');
    ic.textContent = icone;
    const txt = document.createElement('span');
    txt.className = 'bay-toast-text';
    txt.textContent = message;
    el.append(ic, txt);
    let timer: any;
    const fermer = () => {
      clearTimeout(timer);
      el.classList.add('bay-toast-out');
      setTimeout(() => el.remove(), 200);
    };
    (opts.actions || []).forEach(a => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bay-toast-action';
      b.textContent = a.label;
      b.onclick = () => { fermer(); this.ngZone.run(() => a.action()); };
      el.appendChild(b);
    });
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'bay-toast-close';
    x.setAttribute('aria-label', 'Fermer le message');
    x.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">close</span>';
    x.onclick = fermer;
    el.appendChild(x);
    zone.appendChild(el);
    while (zone.children.length > 3) zone.firstElementChild?.remove();
    if (duree > 0) {
      // Pause au survol / focus pour laisser le temps de lire ou d'agir
      const armer = () => { clearTimeout(timer); timer = setTimeout(fermer, duree); };
      el.addEventListener('mouseenter', () => clearTimeout(timer));
      el.addEventListener('mouseleave', armer);
      el.addEventListener('focusin', () => clearTimeout(timer));
      armer();
    }
    return fermer;
  }

  private conteneur(): HTMLElement {
    if (this.zone && document.body.contains(this.zone)) return this.zone;
    const z = document.createElement('div');
    z.className = 'bay-toast-zone';
    z.setAttribute('aria-live', 'polite');
    document.body.appendChild(z);
    this.zone = z;
    return z;
  }
}
